/**
 * useUnansweredQuestions — TanStack Query hook for
 * `contradictions.questions.list`.
 *
 * Fetches LLM-derived press questions that have received no or only partial
 * official answer from WHO/CDC. Every row carries ≥ 2 press source URLs
 * verified at ingest time.
 *
 * Date fields arrive as ISO strings over the wire and are normalised to
 * Date objects here.
 *
 * Prompt #09 (timeline-contradictions-and-unanswered-questions).
 */

import { useQuery } from "@tanstack/react-query";

export type UnansweredQuestionItem = {
  id: string;
  question: string;
  status: "NO_ANSWER" | "PARTIAL" | "ANSWERED";
  lastAskedAt: Date;
  sourceUrls: string[];
  verifiedAt: Date;
};

type RawQuestion = Omit<UnansweredQuestionItem, "lastAskedAt" | "verifiedAt"> & {
  lastAskedAt: string;
  verifiedAt: string;
};

function normalise(raw: RawQuestion): UnansweredQuestionItem {
  return {
    ...raw,
    lastAskedAt: new Date(raw.lastAskedAt),
    verifiedAt: new Date(raw.verifiedAt),
  };
}

async function fetchUnansweredQuestions(
  limit: number,
  apiBaseUrl?: string,
): Promise<UnansweredQuestionItem[]> {
  const base = (
    apiBaseUrl ??
    (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ??
    ""
  ).replace(/\/+$/u, "");

  const encoded = encodeURIComponent(JSON.stringify({ json: { limit } }));
  const url = `${base}/api/trpc/contradictions.questions.list?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });

  if (!res.ok) {
    throw new Error(`contradictions.questions.list failed: ${res.status}`);
  }

  const json = (await res.json()) as {
    result?: {
      data?: { json?: RawQuestion[] } | RawQuestion[];
    };
  };

  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawQuestion[] }).json
      : (inner as RawQuestion[] | undefined);

  if (!data) throw new Error("questions.list: malformed response");
  return data.map(normalise);
}

export function useUnansweredQuestions(opts?: {
  limit?: number;
  apiBaseUrl?: string;
}) {
  const limit = opts?.limit ?? 25;

  return useQuery<UnansweredQuestionItem[]>({
    queryKey: ["unansweredQuestions", "list", limit],
    queryFn: () => fetchUnansweredQuestions(limit, opts?.apiBaseUrl),
    staleTime: 60 * 60 * 1000, // 1 hour — matches the extractor cadence
    retry: 2,
  });
}
